import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Search, 
  Command, 
  LayoutDashboard, 
  Palette, 
  Heart, 
  Crown, 
  TrendingUp, 
  Settings,
  Plus,
  Eye,
  Edit,
  FileText,
  ChevronRight
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface CommandItem {
  id: string
  title: string
  description?: string
  icon: React.ComponentType<{ className?: string }>
  action: () => void
  category: 'navigation' | 'actions' | 'recent' | 'search'
  keywords: string[]
}

interface CommandPaletteProps {
  isOpen: boolean
  onClose: () => void
}

export const CommandPalette = ({ isOpen, onClose }: CommandPaletteProps) => {
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)

  const commands: CommandItem[] = [
    // Navigation
    {
      id: 'nav-dashboard',
      title: 'Dashboard',
      description: 'Panel principal y métricas',
      icon: LayoutDashboard,
      action: () => navigate('/admin'),
      category: 'navigation',
      keywords: ['dashboard', 'inicio', 'métricas', 'panel']
    },
    {
      id: 'nav-products',
      title: 'Productos',
      description: 'Gestionar agendas y papelería',
      icon: Palette,
      action: () => navigate('/admin/products'),
      category: 'navigation',
      keywords: ['productos', 'agendas', 'papelería', 'inventario']
    },
    {
      id: 'nav-orders',
      title: 'Órdenes',
      description: 'Pedidos con amor y cuidado',
      icon: Heart,
      action: () => navigate('/admin/orders'),
      category: 'navigation',
      keywords: ['órdenes', 'pedidos', 'ventas', 'clientes']
    },
    {
      id: 'nav-users',
      title: 'Usuarios',
      description: 'CEOs de sus propias vidas',
      icon: Crown,
      action: () => navigate('/admin/users'),
      category: 'navigation',
      keywords: ['usuarios', 'clientes', 'ceos', 'administradores']
    },
    {
      id: 'nav-analytics',
      title: 'Analytics',
      description: 'Crecimiento y transformación',
      icon: TrendingUp,
      action: () => navigate('/admin/reports'),
      category: 'navigation',
      keywords: ['analytics', 'reportes', 'estadísticas', 'gráficos']
    },
    {
      id: 'nav-settings',
      title: 'Configuración',
      description: 'Ajustes del sistema',
      icon: Settings,
      action: () => navigate('/admin/settings'),
      category: 'navigation',
      keywords: ['configuración', 'ajustes', 'sistema', 'preferencias']
    },

    // Quick Actions
    {
      id: 'action-new-product',
      title: 'Crear Producto',
      description: 'Agregar nueva agenda o papelería',
      icon: Plus,
      action: () => {
        navigate('/admin/products')
        // Trigger new product modal
      },
      category: 'actions',
      keywords: ['crear', 'nuevo', 'producto', 'agenda', 'agregar']
    },
    {
      id: 'action-view-orders',
      title: 'Ver Órdenes Pendientes',
      description: 'Revisar pedidos por procesar',
      icon: Eye,
      action: () => navigate('/admin/orders?status=pending'),
      category: 'actions',
      keywords: ['órdenes', 'pendientes', 'revisar', 'procesar']
    },
    {
      id: 'action-edit-profile',
      title: 'Editar Perfil',
      description: 'Actualizar información personal',
      icon: Edit,
      action: () => console.log('Edit profile'),
      category: 'actions',
      keywords: ['perfil', 'editar', 'información', 'actualizar']
    },
    {
      id: 'action-reports',
      title: 'Generar Reporte',
      description: 'Crear reporte de ventas',
      icon: FileText,
      action: () => navigate('/admin/reports?action=generate'),
      category: 'actions',
      keywords: ['reporte', 'generar', 'ventas', 'exportar']
    }
  ]

  const filteredCommands = commands.filter(command => {
    if (!query) return true
    const searchTerm = query.toLowerCase()
    return (
      command.title.toLowerCase().includes(searchTerm) ||
      command.description?.toLowerCase().includes(searchTerm) ||
      command.keywords.some(keyword => keyword.toLowerCase().includes(searchTerm))
    )
  })

  const groupedCommands = filteredCommands.reduce((acc, command) => {
    if (!acc[command.category]) {
      acc[command.category] = []
    }
    acc[command.category].push(command)
    return acc
  }, {} as Record<string, CommandItem[]>)

  const categoryLabels = {
    navigation: 'Navegación',
    actions: 'Acciones Rápidas',
    recent: 'Recientes',
    search: 'Resultados de Búsqueda'
  }

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  useEffect(() => {
    setSelectedIndex(0)
  }, [query])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return

      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex(prev => Math.min(prev + 1, filteredCommands.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex(prev => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const command = filteredCommands[selectedIndex]
        if (command) {
          command.action()
          onClose()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, selectedIndex, filteredCommands, onClose])

  if (!isOpen) return null

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      
      {/* Command Palette */}
      <div className="fixed top-20 left-1/2 transform -translate-x-1/2 w-full max-w-2xl z-50">
        <div className="bg-white rounded-2xl shadow-2xl border-2 border-admin-border overflow-hidden animate-in slide-in-from-top-4 duration-200">
          {/* Search Input */}
          <div className="flex items-center px-6 py-4 border-b border-admin-border">
            <Search className="h-5 w-5 text-admin-textMuted mr-3" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar páginas, acciones, usuarios..."
              className="flex-1 bg-transparent outline-none text-admin-sidebar placeholder:text-admin-textMuted font-medium"
            />
            <div className="flex items-center space-x-1 text-xs text-admin-textMuted">
              <kbd className="px-2 py-1 bg-admin-border/50 rounded font-mono">⌘</kbd>
              <kbd className="px-2 py-1 bg-admin-border/50 rounded font-mono">K</kbd>
            </div>
          </div>

          {/* Results */}
          <div className="max-h-96 overflow-y-auto">
            {Object.entries(groupedCommands).map(([category, items]) => (
              <div key={category} className="p-2">
                <div className="px-4 py-2 text-xs font-bold text-admin-textMuted uppercase tracking-wider">
                  {categoryLabels[category as keyof typeof categoryLabels]}
                </div>
                
                {items.map((command) => {
                  const globalIndex = filteredCommands.indexOf(command)
                  const isSelected = globalIndex === selectedIndex
                  const Icon = command.icon
                  
                  return (
                    <button
                      key={command.id}
                      onClick={() => {
                        command.action()
                        onClose()
                      }}
                      className={cn(
                        "w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-150 text-left",
                        isSelected
                          ? "bg-admin-accent text-white shadow-lg scale-[1.02]"
                          : "hover:bg-admin-accent/10 text-admin-sidebar"
                      )}
                    >
                      <Icon className={cn(
                        "h-5 w-5 flex-shrink-0",
                        isSelected ? "text-white" : "text-admin-accent"
                      )} />
                      
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">
                          {command.title}
                        </div>
                        {command.description && (
                          <div className={cn(
                            "text-sm truncate",
                            isSelected ? "text-white/80" : "text-admin-textMuted"
                          )}>
                            {command.description}
                          </div>
                        )}
                      </div>
                      
                      <ChevronRight className={cn(
                        "h-4 w-4 flex-shrink-0",
                        isSelected ? "text-white" : "text-admin-textMuted"
                      )} />
                    </button>
                  )
                })}
              </div>
            ))}
            
            {filteredCommands.length === 0 && (
              <div className="p-8 text-center">
                <Search className="h-12 w-12 text-admin-textMuted mx-auto mb-4" />
                <p className="text-admin-textMuted font-medium">No se encontraron resultados</p>
                <p className="text-sm text-admin-textMuted mt-1">Intenta con otros términos de búsqueda</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-3 border-t border-admin-border bg-admin-header/50">
            <div className="flex items-center justify-between text-xs text-admin-textMuted">
              <div className="flex items-center space-x-4">
                <span className="flex items-center space-x-1">
                  <kbd className="px-1.5 py-0.5 bg-admin-border/50 rounded font-mono">↑↓</kbd>
                  <span>navegar</span>
                </span>
                <span className="flex items-center space-x-1">
                  <kbd className="px-1.5 py-0.5 bg-admin-border/50 rounded font-mono">⏎</kbd>
                  <span>seleccionar</span>
                </span>
                <span className="flex items-center space-x-1">
                  <kbd className="px-1.5 py-0.5 bg-admin-border/50 rounded font-mono">Esc</kbd>
                  <span>cerrar</span>
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <Command className="h-3 w-3" />
                <span>Palette</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

